import { useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { makePlaylist } from "../utils/Utils";
import { useLanguage } from "../../context/Language/LanguageContext";
import { matchToVsmJson } from "../utils/DVWToVSM";
import { getVSSMatchSide, orientVsmForHomeTeam } from "../utils/VSSReports";
import {
  evaluateVSSWorkbook,
  formatVSSValue,
  MAX_VSS_FILE_BYTES,
  parseVSSWorkbook,
  vssColumnName,
} from "../utils/VSSWorkbook";

function downloadText(name, content, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function color(value) {
  return typeof value === "string" && /^(#[0-9a-f]{3,8}|[a-z]+)$/i.test(value) ? value : undefined;
}

function border(value) {
  const [borderColor, type] = (value ?? "").split(",");
  const styles = {
    thin: "1px solid", medium: "2px solid", thick: "3px solid",
    dashed: "1px dashed", dotted: "1px dotted", double: "3px double",
  };
  return color(borderColor) && styles[type] ? `${styles[type]} ${borderColor}` : undefined;
}

function cellStyle(sheet, cell, value) {
  const encoded = sheet.content.styles?.find((entry) => entry[0] === cell?.style)?.[1];
  const parts = typeof encoded === "string" ? encoded.split(";") : [];
  const style = {
    color: color(parts[0]),
    backgroundColor: color(parts[1]),
    textAlign: ["left", "right", "center"].includes(parts[2]) ? parts[2] : undefined,
    fontSize: /^\d{1,2}px$/.test(parts[4] ?? "") ? parts[4] : undefined,
    fontStyle: parts[6] === "italic" ? "italic" : undefined,
    fontWeight: parts[7] === "bold" ? "bold" : undefined,
    ...(typeof value === "number" && parts[11] === "percent"
      ? { whiteSpace: "nowrap", overflowWrap: "normal" } : {}),
  };
  // Webix serializes borders in right, top, left, bottom order.
  ["Right", "Top", "Left", "Bottom"].forEach((side, index) => {
    const value = border(parts[12 + index]);
    if (value) style[`border${side}`] = value;
  });
  const conditionColors = {
    webix_ssheet_condition_good: "#b7e1cd",
    webix_ssheet_condition_teal: "#a2c4c9",
    webix_ssheet_condition_neutral: "#ffe599",
    webix_ssheet_condition_grey: "#d9d9d9",
    webix_ssheet_condition_purple: "#d9d2e9",
    webix_ssheet_condition_bad: "#f4cccc",
  };
  for (const condition of sheet.content.conditions ?? []) {
    if (!Array.isArray(condition)) continue;
    if (Number(condition[0]) === cell?.row && Number(condition[1]) === cell?.column &&
      condition[2] === "equal" && String(condition[3]) === String(value)) {
      style.backgroundColor = conditionColors[condition[4]] ?? style.backgroundColor;
    }
  }
  return style;
}

export function VSSSheet({ sheet, onEventsSelected, translate = (key) => key }) {
  const isDistribution = sheet.name.toLowerCase() === "distribution";
  const separatorRows = new Set(isDistribution
    ? [6, 12, 18, 24, 30, 36].flatMap((start) => [start, start + 2, start + 4]) : []);
  const clearedTopRows = new Set(isDistribution
    ? [6, 12, 18, 24, 30, 36].flatMap((start) => [start + 2, start + 4]) : []);
  const hidden = sheet.content.table?.hidden;
  const hiddenColumns = new Set(Array.isArray(hidden?.column) ? hidden.column.map(Number) : []);
  const hiddenRows = new Set(Array.isArray(hidden?.row) ? hidden.row.map(Number) : []);
  const columns = Array.from({ length: sheet.columnCount }, (_, index) => index + 1)
    .filter((column) => !hiddenColumns.has(column));
  const rows = Array.from({ length: sheet.rowCount }, (_, index) => index + 1)
    .filter((row) => !hiddenRows.has(row));
  const sizes = new Map((sheet.content.sizes ?? []).filter(Array.isArray)
    .filter(([type, , size]) => type === 0 && Number.isFinite(size) && size > 0)
    .map(([, column, size]) => [column, Math.min(500, size)]));
  if (isDistribution) {
    for (let start = 2; start + 5 <= sheet.columnCount; start += 7) {
      for (const column of [start, start + 2, start + 4]) {
        sizes.set(column, (sizes.get(column) ?? 80) * 2.25);
      }
    }
  }
  const merged = new Map();
  const covered = new Set();
  for (const [row, column, width, height] of sheet.content.spans ?? []) {
    const spanRows = rows.filter((n) => n >= row && n < row + height);
    const spanColumns = columns.filter((n) => n >= column && n < column + width);
    if (!spanRows.length || !spanColumns.length) continue;
    const anchor = `${vssColumnName(spanColumns[0])}${spanRows[0]}`;
    merged.set(anchor, { address: `${vssColumnName(column)}${row}`, rows: spanRows.length, columns: spanColumns.length });
    for (const r of spanRows) {
      for (const c of spanColumns) {
        const address = `${vssColumnName(c)}${r}`;
        if (address !== anchor) covered.add(address);
      }
    }
  }
  const width = columns.reduce((total, column) => total + (sizes.get(column) ?? 80), 0);
  return (
    <div className="overflow-x-auto">
      <table
        aria-label={sheet.name}
        className="vss-sheet border-collapse text-black bg-white text-xs"
        style={{ tableLayout: "fixed", width, minWidth: width }}
      >
        <colgroup>
          {columns.map((column) => (
            <col key={column} style={{ width: `${((sizes.get(column) ?? 80) / width) * 100}%` }} />
          ))}
        </colgroup>
        <tbody>
          {rows.map((row) => (
            <tr key={row}>
              {columns.map((column) => {
                const address = `${vssColumnName(column)}${row}`;
                if (covered.has(address)) return null;
                const span = merged.get(address);
                const source = span?.address ?? address;
                const cell = sheet.cells.get(source);
                const value = sheet.values.get(source);
                const inDistributionTable = isDistribution && column >= 2 && column <= 49 && (column - 2) % 7 < 6;
                return (
                  <td
                    key={column}
                    title={`${sheet.name}!${source}${typeof cell?.value === "string" && cell.value.startsWith("=") ? `: ${cell.value}` : ""}`}
                    colSpan={span?.columns}
                    rowSpan={span?.rows}
                    style={{
                      padding: "4px", height: "24px", whiteSpace: "pre-wrap", overflowWrap: "anywhere",
                      border: sheet.content.table?.gridlines ? "1px solid #d1d5db" : undefined,
                      ...cellStyle(sheet, cell, value),
                      ...(inDistributionTable && clearedTopRows.has(row)
                        ? { borderTop: "none" } : {}),
                      ...(inDistributionTable && clearedTopRows.has(row + (span?.rows ?? 1))
                        ? { borderBottom: "none" } : {}),
                      ...(inDistributionTable && separatorRows.has(row)
                        ? { borderBottom: "1px solid #000000" } : {}),
                    }}
                  >
                    {onEventsSelected && typeof value === "number" && sheet.eventSources?.has(source) ? (
                      <button type="button" className="underline decoration-dotted print:no-underline"
                        aria-label={`${translate("Create playlist for")} ${sheet.name}!${source}: ${formatVSSValue(value, cell, sheet.content)}`}
                        onClick={() => onEventsSelected(sheet.eventSources.get(source))}>
                        {formatVSSValue(value, cell, sheet.content)}
                      </button>
                    ) : formatVSSValue(value, cell, sheet.content)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VSSReports({ match, matches, team, selectedTeam = 0, selectedGame = 0 }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuthStatus();
  const restored = location.state?.vssReport;
  const [templates, setTemplates] = useState(() => restored?.templates ?? []);
  const [selectedId, setSelectedId] = useState(() => restored?.selectedId ?? "");
  const [sheetIndex, setSheetIndex] = useState(() => restored?.sheetIndex ?? 0);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const reportRef = useRef(null);
  const uploadVersion = useRef(0);
  const template = templates.find((item) => item.id === selectedId);
  const translateNotice = (message) => {
    const missing = /^Some matching plays have no recorded (.*); those plays are excluded from filters requiring it\.$/.exec(message);
    return missing
      ? t("Some matching plays have no recorded {field}; those plays are excluded from filters requiring it.").replace("{field}", t(missing[1]))
      : t(message);
  };
  const side = selectedTeam === 1 ? "away" : "home";
  const sourceMatches = useMemo(() => matches ?? (match ? [match] : []), [matches, match]);
  const multiple = sourceMatches.length > 1;
  const unsupported = sourceMatches.some((item) => item.app === "VBStats");
  const teamName = team
    ? selectedTeam === 1 ? t("Opponents") : team
    : (side === "home" ? match?.teamA.Name : match?.teamB.Name) ?? "";
  const scope = `${selectedGame ? `${t("Set")} ${selectedGame}` : t("All sets")}${multiple ? ` / ${sourceMatches.length} ${t("matches")}` : ""}`;
  const result = useMemo(() => {
    if (!template || !sourceMatches.length || unsupported) return null;
    try {
      return evaluateVSSWorkbook(template.workbook, sourceMatches, { side, selectedGame, team, allSettingSetters: true });
    } catch (error) {
      console.error("Could not calculate VSS template:", error);
      return { error: error.message };
    }
  }, [template, sourceMatches, unsupported, side, selectedGame, team]);
  const printReport = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `${template?.name ?? "VolleyStation report"} - ${teamName}`,
    pageStyle: "@page { size: landscape; margin: 10mm; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .overflow-x-auto { overflow: visible !important; } .vss-sheet { width: 100% !important; min-width: 0 !important; break-inside: auto; } tr { break-inside: avoid; } }",
    onPrintError: (location, error) => {
      console.error("Could not print VSS report:", location, error);
      toast.error(t("Could not print the report."));
    },
  });

  const importTemplates = async (event) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!files.length) return;
    const version = ++uploadVersion.current;
    setLoading(true);
    setUploadError("");
    try {
      const imported = [];
      for (const file of files) {
        if (!/\.vss$/i.test(file.name)) throw new Error(t("Please select a .vss template file."));
        if (file.size > MAX_VSS_FILE_BYTES) throw new Error(t("VSS templates must be smaller than 5 MB."));
        const source = await file.text();
        imported.push({
          id: `${version}-${imported.length}`, name: file.name, source,
          workbook: parseVSSWorkbook(source),
        });
      }
      if (version !== uploadVersion.current) return;
      setTemplates(imported);
      setSelectedId(imported[0].id);
      setSheetIndex(0);
    } catch (error) {
      if (version !== uploadVersion.current) return;
      console.error("Could not load VSS template:", error);
      setUploadError(error.message);
      toast.error(t("Could not load the VSS template."));
    } finally {
      if (version === uploadVersion.current) setLoading(false);
    }
  };

  const exportVsm = () => {
    try {
      const files = sourceMatches.map((item, index) => {
        const matchSide = getVSSMatchSide(item, team, side);
        if (selectedGame && !item.sets?.[selectedGame - 1]) throw new Error("The selected set does not exist.");
        const source = selectedGame ? { ...item, sets: [item.sets[selectedGame - 1]] } : item;
        const vsm = orientVsmForHomeTeam(matchToVsmJson(source), matchSide);
        const name = (matchSide === "home" ? item.teamA.Name : item.teamB.Name).replace(/[^a-z0-9_-]+/gi, "-");
        return { name: `${name}${multiple ? `-${index + 1}` : ""}.vsm`, source: JSON.stringify(vsm, null, 2) };
      });
      for (const file of files) downloadText(file.name, file.source, "application/json");
    } catch (error) {
      console.error("Could not export VolleyStation match:", error);
      toast.error(t("Could not export the VolleyStation match file."));
    }
  };

  const createPlaylist = (events) => {
    if (!events.length) {
      toast.info(t("No matching events make up this value."));
      return;
    }
    try {
      const playlist = makePlaylist(events);
      localStorage.setItem("VBLivePlaylistEvents", JSON.stringify(playlist));
      navigate(location.pathname, { replace: true, state: {
        ...location.state,
        vssReport: { templates, selectedId, sheetIndex, selectedTeam, selectedGame },
      } });
      navigate("/playlist", { state: {
        playlistFileData: JSON.stringify({ events: playlist }),
        filename: null, playlists: null, serverName: currentUser?.email,
      } });
    } catch (error) {
      console.error("Could not create VSS report playlist:", error);
      toast.error(t("Could not create the report-events playlist."));
    }
  };

  if (unsupported) {
    return <div className="alert alert-info m-4">{t("VolleyStation templates require DataVolley or VolleyStation match files for every selected match.")}</div>;
  }
  if (!sourceMatches.length) return <p role="status" className="p-4">{t("Loading matches...")}</p>;
  const sheet = result?.sheets?.[sheetIndex];
  return (
    <div className="space-y-4 p-4">
      <div>
        <label className="block font-semibold mb-2" htmlFor="vss-template-files">{t("Select VSS templates")}</label>
        <input
          id="vss-template-files" type="file" accept=".vss" multiple
          className="file-input file-input-bordered w-full max-w-lg"
          onChange={importTemplates} aria-busy={loading}
        />
        <p className="mt-2 text-sm opacity-70">
          {t("Choose your own VolleyStation spreadsheet templates. Files are processed locally, not uploaded.")}
        </p>
        <p className="mt-1 text-sm">
          {t("Template home team")}: {teamName} / {scope}.
          {" "}{t("Use the team and set selectors above to change the report data.")}
        </p>
      </div>
      {loading && <p role="status">{t("Reading VSS templates...")}</p>}
      {uploadError && <div role="alert" className="alert alert-error">{uploadError}</div>}
      {template && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            {t("Template")}
            <select
              className="select select-sm select-bordered" value={selectedId} disabled={loading}
              onChange={(event) => { setSelectedId(event.target.value); setSheetIndex(0); }}
            >
              {templates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => printReport()}
            disabled={loading || !sheet || result?.diagnostics?.length > 0}>
            {t("Print / save PDF")}
          </button>
          <button type="button" className="btn btn-sm btn-outline" disabled={loading} onClick={exportVsm}>
            {t(multiple ? "Download VolleyStation .vsm files" : "Download VolleyStation .vsm")}
          </button>
          <button type="button" className="btn btn-sm btn-outline" disabled={loading}
            onClick={() => downloadText(template.name, template.source, "application/json")}>
            {t("Download selected template")}
          </button>
        </div>
      )}
      {result?.error && <div role="alert" className="alert alert-error">{result.error}</div>}
      {result?.diagnostics?.length > 0 && (
        <div role="alert" className="rounded border border-error p-3">
          <p className="font-semibold">{t("This template could not be fully calculated. PDF export is disabled.")}</p>
          <ul className="list-disc ml-5 text-sm">
            {result.diagnostics.slice(0, 20).map((issue) => (
              <li key={`${issue.sheet}!${issue.cell}`}>{issue.sheet}!{issue.cell}: {issue.message}</li>
            ))}
          </ul>
          {result.diagnostics.length > 20 && <p>{result.diagnostics.length - 20} {t("more cell errors.")}</p>}
        </div>
      )}
      {result?.warnings?.length > 0 && (
        <div role="status" className="alert alert-warning">
          <ul>{result.warnings.map((warning) => <li key={warning}>{translateNotice(warning)}</li>)}</ul>
        </div>
      )}
      {sheet && (
        <>
          <div className="flex flex-wrap gap-2" role="tablist" aria-label={t("Workbook sheets")}>
            {result.sheets.map((item, index) => (
              <button type="button" role="tab" aria-selected={sheetIndex === index}
                key={item.name} className={`btn btn-sm ${sheetIndex === index ? "btn-active" : "btn-ghost"}`}
                onClick={() => setSheetIndex(index)}>
                {item.name}
              </button>
            ))}
          </div>
          <div ref={reportRef} className="bg-white text-black p-3">
            <h2 className="font-bold mb-1">{template.name} / {sheet.name}</h2>
            <p className="text-sm mb-3">{teamName} / {scope}</p>
            {result.warnings.length > 0 && <p className="text-xs mb-3">{result.warnings.map(translateNotice).join(" ")}</p>}
            <VSSSheet sheet={sheet} onEventsSelected={createPlaylist} translate={t} />
          </div>
        </>
      )}
      {!template && !loading && <p className="opacity-70">{t(multiple ? "Select a .vss file to run its formulas against these matches." : "Select a .vss file to run its formulas against this match.")}</p>}
    </div>
  );
}

export default VSSReports;
